import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  Icon,
  Illustration,
  Loader,
  Logo,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import useGetIDPURL from '../../../../hooks/use-getidpurl';
import { useInvertedLocalize } from '../../../../utils';
import { ERROR_MESSAGE } from '../../../../utils/constants';

import invertedMessages from './messages';

import './IDPAnonymous.css';

export default function IDPAnonymous() {
  const l = useInvertedLocalize(invertedMessages);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorBanner, setShowErrroBanner] = useState(false);
  const [isDisableIDProofingBtn, setIsDisableIDProofingBtn] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { mutate: getIDPURL, isPending } = useGetIDPURL();
  const params = new URLSearchParams(window.location.search);
  const key = params.get('key') ?? '';

  const startIdProofingHandler = useCallback(
    (validateLink = false) => {
      if (validateLink) {
        setIsLoading(true);
      }

      getIDPURL(
        { key, validateLink },
        {
          onSuccess(response) {
            if (response.error && response.error.status === 2) {
              setIsLinkExpired(true);
            } else {
              setIsLinkExpired(false);

              if (!response.ok && response.status !== 0) {
                setErrorMessage(
                  response.error.errorMessage ||
                    response.errorMessage ||
                    response.error.idProofingUrl ||
                    ERROR_MESSAGE
                );
                setIsDisableIDProofingBtn(true);
                setShowErrroBanner(true);
              } else if (!validateLink) {
                const { idProofingUrl } = response;

                if (idProofingUrl) {
                  window.open(idProofingUrl, '_blank');
                  setIsDisableIDProofingBtn(true);
                }
              }

              setIsOpenConfirmationModal(false);
            }

            setIsLoading(false);
          },
          onError() {
            setIsLoading(false);
            setErrorMessage(ERROR_MESSAGE);
            setShowErrroBanner(true);
            setIsDisableIDProofingBtn(true);
          },
        }
      );
    },
    [getIDPURL, key]
  );

  // By default body has padding-bottom set to 3 rem
  useEffect(() => {
    document.body.classList.add('no-bottom-padding');

    return () => {
      document.body.classList.remove('no-bottom-padding');
    };
  }, []);

  useEffect(() => {
    startIdProofingHandler(true);
  }, [startIdProofingHandler]);

  if (isLoading) {
    return (
      <Box className='Ext-IDP-anonymousPage-loader'>
        <Loader kind='loop' format='main' color='brand' />
      </Box>
    );
  }

  return (
    <Box className='Ext-IDP-anonymousPage'>
      <header>
        <Logo kind='Saviynt' />
      </header>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showErrorBanner}
        onCancel={() => setShowErrroBanner(false)}
        isColonVisible={false}
      />
      <ModalIsland
        kind='warning'
        alertTitle={l('One-time access only!')}
        alertSubtitle={l(
          'This link can only be accessed once. After clicking, it will no longer be available. Please keep your information ready for identity proofing before proceeding.'
        )}
        onClose={() => setIsOpenConfirmationModal(false)}
        isOpen={isOpenConfirmationModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={isPending}
            onClick={() => startIdProofingHandler(false)}>
            {l('Apply')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            onClick={() => setIsOpenConfirmationModal(false)}
            size='medium'>
            {l('Cancel')}
          </Button>
        }
      />
      {!isLinkExpired ? (
        <div className='Ext-IDP-anonymousPage-content'>
          <Typography
            kind='display2-light'
            color='--color-foreground-secondary-bold'
            className='Ext-IDP-anonymousPage-content-welcomeText'>
            {l('Welcome to ID Proofing')}
          </Typography>
          <Typography
            kind='body1'
            color='--color-foreground-neutral-subtle'
            className='Ext-IDP-anonymousPage-content-subText'>
            {l(
              'To begin the ID proofing process, you will be securely redirected to Identity Proofing website to complete your identity verification.'
            )}
          </Typography>
          <div className='Ext-IDP-anonymousPage-content-btn'>
            <Button
              kind='filled'
              size='medium'
              isLoading={isPending}
              loaderText={l('Start ID Proofing')}
              rightIcon={<Icon kind='ArrowRight' />}
              isDisabled={isPending || isDisableIDProofingBtn}
              onClick={() => setIsOpenConfirmationModal(true)}>
              {l('Start ID Proofing')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='Ext-IDP-anonymousPage-content'>
          <Typography
            kind='display2-light'
            className='Ext-IDP-anonymousPage-content-expiredText'
            color='--color-foreground-secondary-bold'>
            {l('ID Proofing Setup Session Expired')}
          </Typography>
          <Illustration
            kind='Error'
            className='Ext-IDP-anonymousPage-content-expiredIllustration'
          />
          <Typography
            kind='body1'
            className='Ext-IDP-anonymousPage-content-expiredSubText'>
            {l(
              'If you need to access the below link to this page again, please reach out to your organization’s support team.'
            )}
          </Typography>
        </div>
      )}
    </Box>
  );
}
